import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { ThemePalette } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule, ProgressSpinnerMode } from '@angular/material/progress-spinner';
import { MatRadioChange, MatRadioModule } from '@angular/material/radio';
import { MatToolbarModule } from '@angular/material/toolbar';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Actions, ofActionDispatched, Store } from '@ngxs/store';
import { filter } from 'rxjs/operators';
import { ContextMenuComponent } from '../../features/scm/material-management/v1/buildingBlocks/context-menu/context-menu.component';
import { ContextMenuItem } from '../../features/scm/material-management/v1/buildingBlocks/context-menu/context-menu.types';
import { TOOLBAR_TEST_IDS, USER_MENU_DEFS } from './toolbar.consts';
import { UserMenuAction } from './toolbar.types';

import {
  AuthenticationSelectors,
  ConfigService,
  LanguageSelectors,
  LogoutAdfs,
  NotificationService,
  Plant,
  SetActivePlant,
  SetLanguage,
  SetUserRightsByRole,
} from '@orion-platform/core';
import { SVNotification } from '../../features/scm/material-management/v1/models/sv-notifications.model';
import { ResetAllDefaultFields } from '../../features/scm/material-management/v1/store/common/common.actions';
import { StorageKeys } from '../../features/scm/material-management/v1/types';
import {
  GetNotificationsError,
  GetNotificationsSuccess,
  GetNotificationsUnreadCountSuccess,
  ResetPlantDependentCache,
  ShowNotifications,
} from '../../features/scm/material-management/v1/store/supply-visibility/supply-visibility.actions';
import { SVSelectors } from '../../features/scm/material-management/v1/store/supply-visibility/supply-visibility.selectors';
import { EnumDataStatus, Helper } from '../../helper/helper';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    RouterModule,
    TranslatePipe,
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatRadioModule,
    MatBadgeModule,
    MatProgressSpinnerModule,
    ContextMenuComponent,
  ],
  selector: 'orion-platform-toolbar',
  standalone: true,
  styleUrls: ['./toolbar.component.scss'],
  templateUrl: './toolbar.component.html',
})
export class ToolbarComponent implements OnInit {
  // Dependencies
  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly config = inject(ConfigService);
  private readonly actions$ = inject(Actions);
  private readonly notificationService = inject(NotificationService);

  // Inputs converted to Signals
  readonly diameter = input<number>(25);
  readonly value = input<number>();
  readonly mode = input<ProgressSpinnerMode>('indeterminate');
  readonly color = input<ThemePalette>('primary');

  // State Signals
  readonly userName = this.store.selectSignal(AuthenticationSelectors.username);
  readonly langs = this.store.selectSignal(LanguageSelectors.getLangs);
  readonly currentLang = this.store.selectSignal(LanguageSelectors.getCurrentLang);
  readonly plants = this.store.selectSignal(AuthenticationSelectors.plants);
  readonly activePlantCode = this.store.selectSignal(AuthenticationSelectors.activePlant);
  readonly activeRoles = this.store.selectSignal(AuthenticationSelectors.getCurrentRoles);

  // Supply Visibility Signals
  // Assuming SupplyVisibilityState has static selectors. If not, this might need adjustment similar to Auth/Lang.
  // I will assume the original code usage was correct or likely mapped to static similarly.
  // Local State
  // Local State
  notificationsStatus: WritableSignal<EnumDataStatus> = signal(EnumDataStatus.FETCHING);
  notifications: WritableSignal<SVNotification[]> = signal([]);
  notificationsUnreadCount: WritableSignal<number> = signal(0);
  url: string = '';
  urlSegments: string[] = [];

  readonly testIds = TOOLBAR_TEST_IDS;

  // Selector emits '' when state.plant is stale/forbidden, and we
  // need to react to that by replacing the displayed plant with a Show:true fallback.
  readonly activePlant = computed((): null | Plant | undefined => {
    const plantsList = this.plants() as Plant[];
    const code = this.activePlantCode();

    if (!plantsList?.length) {
      return null;
    }

    // keep only plants the user is allowed to see (Show:true) — the rest are not displayed
    const visible = plantsList.filter((p: Plant): boolean => !!p.Show);
    // find the Show:true plant matching activePlantCode — that's the currently selected plant;
    // if no match (selector returned '' because state.plant is invalid), fallback to the first Show:true
    return visible.find((p: Plant): boolean => p.Code === code) || visible[0];
  });

  readonly limitedPlants = computed((): Plant[] => {
    const plantsList = this.plants() as Plant[];
    if (!Array.isArray(plantsList)) return [];
    return plantsList.filter((plant): boolean => !!(plant && plant.Show));
  });

  readonly plantMenuItems = computed((): ContextMenuItem[] => {
    const activeCode = this.activePlant()?.Code;
    return this.limitedPlants().map((plant): ContextMenuItem => ({
      label: `${plant.Code} | ${plant.Name}`,
      name: plant.Code,
      selected: plant.Code === activeCode,
    }));
  });

  /**
   * Maps available languages to context-menu items. Mirrors the plant menu
   * pattern — `selected` flags the current language so the BB renders a
   * filled radio glyph on that row.
   */
  readonly languageMenuItems = computed((): ContextMenuItem[] => {
    const activeCode = this.currentLang()?.code;
    return (this.langs() || []).map((lang): ContextMenuItem => ({
      label: lang.description,
      name: lang.code,
      selected: lang.code === activeCode,
    }));
  });

  readonly documentationLink = computed((): string => {
    const plant = this.activePlant();
    if (!plant || !plant.DivisionCode) return '';
    const linkTemplate = this.config.getSettings('documentationLink') as string;
    if (!linkTemplate) return '';
    return linkTemplate.replace('{DivisionCode}', plant.DivisionCode.toLowerCase());
  });

  userMenuItems: ContextMenuItem[] = [];

  constructor() {}

  ngOnInit(): void {
    this.url = this.router.url;
    this.urlSegments = this.url.split('/').filter(Boolean);

    this.initUserMenu();

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event): void => {
        this.url = event.urlAfterRedirects;
        this.urlSegments = this.url.split('/').filter(Boolean);
      });

    // Initial Notif Status
    const notifications = this.store.selectSnapshot(SVSelectors.getNotificationsList);
    const unreadCount = this.store.selectSnapshot(SVSelectors.getNotificationsUnreadCount);

    if (notifications?.length > 0) {
      this.notifications.set(notifications);
      this.notificationsUnreadCount.set(unreadCount || 0);
      this.setNotificationsStatus(notifications);
    } else {
      this.notificationsStatus.set(EnumDataStatus.FETCHING);
    }

    this.catchGetNotificationsSuccess();
    this.catchGetNotificationsUnreadCountSuccess();
    this.catchGetNotificationsError();
  }

  private initUserMenu(): void {
    this.buildUserMenuItems();
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((): void => this.buildUserMenuItems());
  }

  private buildUserMenuItems(): void {
    this.userMenuItems = USER_MENU_DEFS.map((def): ContextMenuItem => ({
      disabled: def.disabled,
      divider: def.divider,
      icon: def.icon,
      label: def.labelKey ? this.translate.instant(def.labelKey) : '',
      name: def.name,
    }));
  }

  onUserMenuAction(action: string): void {
    switch (action as UserMenuAction) {
      case UserMenuAction.AdRoles:
        this.showAdRoles();
        break;
      case UserMenuAction.Logout:
        this.logout();
        break;
    }
  }

  setNotificationsStatus(notifications?: SVNotification[]): void {
    if (notifications) {
      if (notifications.length) {
        this.notificationsStatus.set(EnumDataStatus.AVAILABLE);
      } else {
        this.notificationsStatus.set(EnumDataStatus.NONE);
      }
    } else {
      this.notificationsStatus.set(EnumDataStatus.ERROR);
    }
  }

  onLanguageMenuAction(langCode: string): void {
    this.applyLanguageByCode(langCode);
  }

  private applyLanguageByCode(langCode: string): void {
    const lang = (this.langs() || []).find((l): boolean => l.code === langCode);
    if (!lang) return;
    this.translate.use(lang.code);
    this.store.dispatch(new SetLanguage(lang));
    localStorage.setItem(StorageKeys.LANGUAGE, lang.code);
  }

  resetAllState(): void {
    this.store.dispatch(new ResetPlantDependentCache());
    this.store.dispatch(new ResetAllDefaultFields());
  }

  setActivePlant(event: MatRadioChange): void {
    this.activatePlantByCode((event.value as Plant).Code);
  }

  onPlantMenuAction(plantCode: string): void {
    this.activatePlantByCode(plantCode);
  }

  private activatePlantByCode(plantCode: string): void {
    this.store.dispatch(new SetActivePlant(plantCode)).subscribe((): void => {
      this.store.dispatch(new SetUserRightsByRole());
      this.resetAllState();
      const currentPath: string = Helper.getUrlPaths();
      const redirectPath = '/dashboard';
      if (currentPath !== redirectPath) {
        this.router.navigate([redirectPath]);
      } else {
        location.reload();
      }
    });
  }

  logout(): void {
    this.store.dispatch(new LogoutAdfs());
  }

  showAdRoles(): void {
    const roles = this.activeRoles();
    if (roles) {
      this.notificationService.showMessage(roles.join(', '));
    }
  }

  middleClickHandle(url: string, event: MouseEvent): void {
    if (event.which === 2 || event.buttons === 4) {
      Helper.openInNewTab(url, this.router);
    }
  }

  goToNotifications(id: string): void {
    this.store.dispatch(new ShowNotifications(id));
  }

  catchGetNotificationsSuccess(): void {
    this.actions$
      .pipe(ofActionDispatched(GetNotificationsSuccess), takeUntilDestroyed(this.destroyRef))
      .subscribe((res): void => {
        this.setNotificationsStatus(res.notifications);
        this.notifications.set(res.notifications || []);
      });
  }

  catchGetNotificationsUnreadCountSuccess(): void {
    this.actions$
      .pipe(
        ofActionDispatched(GetNotificationsUnreadCountSuccess),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((res): void => {
        this.notificationsUnreadCount.set(res.count);
      });
  }

  catchGetNotificationsError(): void {
    this.actions$
      .pipe(ofActionDispatched(GetNotificationsError), takeUntilDestroyed(this.destroyRef))
      .subscribe((): void => {
        this.setNotificationsStatus();
      });
  }
}
